import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareBar from "@/components/feature/ShareBar";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = newsList.find((n) => n.id === id);

  usePageMeta(
    news ? `${news.title} - 火狼动漫网` : "资讯不存在 - 火狼动漫网",
    news?.summary,
  );

  if (!news) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <div className="max-w-2xl mx-auto flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">资讯不存在或已删除</h1>
          <p className="text-sm text-foreground-500 mb-6">你访问的资讯可能已被移除，或链接有误。</p>
          <Link
            to="/news"
            className="px-6 h-11 inline-flex items-center rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.id !== news.id && n.category === news.category)
    .concat(newsList.filter((n) => n.id !== news.id && n.category !== news.category))
    .slice(0, 3);

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "动漫资讯", to: "/news" }, { label: news.title }]} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2 min-w-0">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs font-semibold mb-4">
              {news.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-snug mb-4">
              {news.title}
            </h1>

            <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-500 pb-5 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {news.author}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {news.publishedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-newspaper-line"></i>
                </span>
                来源：{news.source}
              </span>
            </div>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 my-6">
              <img
                src={news.cover}
                alt={`${news.title} 配图`}
                title={`${news.title} - 火狼动漫网动漫资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="text-base text-foreground-700 leading-loose mb-5 font-medium">{news.summary}</p>

            <div className="space-y-5">
              {news.content.map((p, i) => (
                <p key={i} className="text-base text-foreground-700 leading-loose">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-5 border-t border-background-200">
              <div className="flex items-center flex-wrap gap-2">
                {news.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs"
                  >
                    # {t}
                  </span>
                ))}
              </div>
              <ShareBar title={news.title} />
            </div>
          </article>

          <aside>
            <div className="sticky top-20">
              <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                相关资讯推荐
              </h2>
              <div className="space-y-4">
                {related.map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>
              <Link
                to="/news"
                className="mt-5 flex items-center justify-center h-11 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看更多资讯
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}